import { getMapfreDestinationZone } from '@/constants/travelCountries';
import { findTravelProduct, getDefaultCoverageLimits } from '@/constants/travelProducts';

function formatLocalIso(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Today in local timezone (YYYY-MM-DD). */
export function travelTodayLocalIso() {
  return formatLocalIso(new Date());
}

export function usdToKsh(usdAmount, rate) {
  const usd = Number(usdAmount) || 0;
  const fx = Number(rate) || 130;
  return Math.ceil(Number((usd * fx).toFixed(2)));
}

export function travelDaysBetween(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diff = end - start;
  if (Number.isNaN(diff) || diff < 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1;
}

/** Earliest selectable travel start date (today, local timezone). */
export function minTravelStartDate() {
  return travelTodayLocalIso();
}

export function isValidTravelStartDate(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  return dateStr >= minTravelStartDate();
}

export function travelStartDateError(dateStr) {
  if (!dateStr) return '';
  if (!isValidTravelStartDate(dateStr)) {
    return 'Travel start date cannot be in the past.';
  }
  return '';
}

export function travelDateRangeError(startDate, endDate) {
  const startErr = travelStartDateError(startDate);
  if (startErr) return startErr;
  if (!startDate || !endDate) return '';
  if (travelDaysBetween(startDate, endDate) <= 0) {
    return 'End date must be on or after start date.';
  }
  return '';
}

export function emptyQuoteTraveller(isMain = false) {
  return {
    dateOfBirth: '',
    isMain,
    isTraveling: true,
  };
}

/** Normalize to YYYY-MM-DD for date inputs and comparisons. */
export function normalizeTravelDob(value) {
  if (!value) return '';
  if (Array.isArray(value)) {
    const [y, m, d] = value;
    if (y && m && d) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
  const parsed = new Date(s);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return '';
}

export function isValidPastDob(dob) {
  const normalized = normalizeTravelDob(dob);
  if (!normalized) return false;
  const today = new Date().toISOString().slice(0, 10);
  return normalized <= today;
}

export const TRAVEL_PASSPORT_MIN_LEN = 7;
export const TRAVEL_PASSPORT_MAX_LEN = 10;

/** Strip spaces/symbols and cap length — letters + digits, up to 10 characters. */
export function normalizeTravelPassport(value) {
  return String(value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, TRAVEL_PASSPORT_MAX_LEN);
}

export function isValidTravelPassport(value) {
  const passport = normalizeTravelPassport(value);
  if (!passport || passport.startsWith('TBC')) return false;
  if (passport.length < TRAVEL_PASSPORT_MIN_LEN || passport.length > TRAVEL_PASSPORT_MAX_LEN) {
    return false;
  }
  // 1–4 letters then digits (e.g. A1234567, AB123456).
  return /^[A-Z]{1,4}[0-9]+$/.test(passport);
}

export function travelPassportError(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'Passport is required.';
  const cleaned = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (cleaned.length > TRAVEL_PASSPORT_MAX_LEN) {
    return `Passport must be at most ${TRAVEL_PASSPORT_MAX_LEN} characters (e.g. A123456789).`;
  }
  if (!isValidTravelPassport(cleaned)) {
    return `Enter a valid passport (${TRAVEL_PASSPORT_MIN_LEN}–${TRAVEL_PASSPORT_MAX_LEN} characters, letters then digits, e.g. A1234567 or AB123456).`;
  }
  return '';
}

/** Mapfre-facing passport — valid normalized value or a short placeholder. */
export function resolveMapfrePassport(value, fallback = 'TBCPH') {
  if (isValidTravelPassport(value)) return normalizeTravelPassport(value);
  return fallback;
}

/** Clamp stored passport values (e.g. from session) to valid normalized form. */
export function sanitizeTravelPassportFields(row) {
  if (!row || typeof row !== 'object') return row;
  const passport = row.passport ? normalizeTravelPassport(row.passport) : '';
  return { ...row, passport };
}

function travelRelationshipForGis(traveller) {
  if (traveller?.isAccountHolder && traveller.isTraveling === false) return 'POLICYHOLDER';
  if (traveller?.isTraveling === false) return 'NON_TRAVELLING';
  if (traveller?.isMain) return 'SELF';
  return 'TRAVELLER';
}

/** Canonical traveller rows for quotations-api travellersJson (GIS TQ register). */
export function serializeTravelTravellersForApi(insureds = []) {
  return (insureds || []).map((t) => {
    const surname = String(t.surname || '').trim();
    const firstName = String(t.firstName || '').trim();
    const passport = t.passport ? normalizeTravelPassport(t.passport) : '';
    // Legacy RFQ payloads may still send snake_case id_no.
    // eslint-disable-next-line camelcase
    const legacyIdNo = t.id_no;
    const idNumber = String(t.idNumber || t.nationalId || t.idNo || legacyIdNo || '').trim() || passport;
    return {
      firstName,
      surname,
      lastName: surname,
      last_name: surname,
      dateOfBirth: normalizeTravelDob(t.dateOfBirth) || '',
      passport,
      idNumber,
      isMain: !!t.isMain,
      isTraveling: t.isTraveling !== false,
      isAccountHolder: !!t.isAccountHolder,
      identityFromIprs: !!t.identityFromIprs,
      relationship: travelRelationshipForGis(t),
    };
  });
}

export const TRAVEL_POLICYHOLDER_MIN_AGE = 18;

export function travelAgeFromDob(dob) {
  const normalized = normalizeTravelDob(dob);
  if (!normalized) return null;
  const birth = new Date(`${normalized}T12:00:00`);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

export function isTravelAdult(dob) {
  const age = travelAgeFromDob(dob);
  return age != null && age >= TRAVEL_POLICYHOLDER_MIN_AGE;
}

export function isTravelMinor(dob) {
  const age = travelAgeFromDob(dob);
  return age != null && age < TRAVEL_POLICYHOLDER_MIN_AGE;
}

export const COVER_FOR_SELF = 'self';
export const COVER_FOR_OTHER = 'other';

/** Restore whether the logged-in customer is on the trip (legacy quotes used coverFor). */
export function inferAccountHolderTravelling(source = {}) {
  if (source.accountHolderTravelling != null) return !!source.accountHolderTravelling;
  if (source.coverFor === COVER_FOR_OTHER) return false;
  if (source.coverFor === COVER_FOR_SELF) return true;
  const dobs = (source.travellers || source.quoteTravellers || [])
    .map((t) => normalizeTravelDob(t.dateOfBirth))
    .filter(Boolean);
  if (dobs.length === 1 && isTravelMinor(dobs[0])) return false;
  return true;
}

export function coverForFromAccountHolderTravelling(accountHolderTravelling) {
  return accountHolderTravelling ? COVER_FOR_SELF : COVER_FOR_OTHER;
}

/** True when travelling party has minor(s) but no travelling adult — needs separate policyholder. */
export function needsSeparateAdultPolicyholder(quoteTravellers = []) {
  const travelling = (quoteTravellers || []).filter((t) => t.isTraveling !== false);
  if (!travelling.length) return false;
  const hasMinor = travelling.some((t) => t.dateOfBirth && isTravelMinor(t.dateOfBirth));
  if (!hasMinor) return false;
  const hasTravelingAdult = travelling.some((t) => t.dateOfBirth && isTravelAdult(t.dateOfBirth));
  return !hasTravelingAdult;
}

/** Policyholder row — prefers isMain, then first adult. */
export function findPolicyholderTraveller(travellers = []) {
  const list = travellers || [];
  const main = list.find((t) => t.isMain);
  if (main) return main;
  return list.find((t) => isTravelAdult(t.dateOfBirth)) || list[0] || null;
}

export function validateTravelPolicyholder(travellers = []) {
  const traveling = (travellers || []).filter((t) => t.isTraveling !== false);
  if (!traveling.length) {
    return { valid: false, message: 'At least one travelling person is required.' };
  }
  const holder = findPolicyholderTraveller(travellers);
  if (!holder?.dateOfBirth) {
    return { valid: false, message: 'Policyholder date of birth is required.' };
  }
  if (!isTravelAdult(holder.dateOfBirth)) {
    return {
      valid: false,
      message: 'Policyholder must be at least 18 years old. Select an adult as policyholder.',
    };
  }
  return { valid: true, policyholder: holder };
}

/**
 * When minors travel, clear isMain from minors and assign an adult as policyholder.
 */
export function reconcileTravelPolicyholder(travellers = [], { preferIndex } = {}) {
  const rows = (travellers || []).map((t) => ({ ...t }));
  if (!rows.length) return rows;

  const travelingMinors = rows.some(
    (t) => t.isTraveling !== false && t.dateOfBirth && isTravelMinor(t.dateOfBirth),
  );
  if (!travelingMinors) {
    if (!rows.some((t) => t.isMain)) rows[0].isMain = true;
    return rows;
  }

  rows.forEach((traveller, i) => {
    if (traveller.dateOfBirth && isTravelMinor(traveller.dateOfBirth)) {
      rows[i] = { ...traveller, isMain: false };
    }
  });

  let mainIdx = rows.findIndex((t) => t.isMain && isTravelAdult(t.dateOfBirth));
  if (mainIdx < 0 && preferIndex != null && isTravelAdult(rows[preferIndex]?.dateOfBirth)) {
    mainIdx = preferIndex;
  }
  if (mainIdx < 0) {
    mainIdx = rows.findIndex((t) => isTravelAdult(t.dateOfBirth));
  }
  if (mainIdx >= 0) {
    return rows.map((traveller, i) => ({ ...traveller, isMain: i === mainIdx }));
  }
  return rows;
}

/** Map IPRS name parts to first name + surname (surname includes other/middle names). */
export function formatIprsTravellerNames(iprs) {
  if (!iprs) return { firstName: '', surname: '' };

  const clean = (v) => (v && v !== 'null' ? String(v).trim() : '');
  const upper = (v) => clean(v).toUpperCase();

  const firstName = upper(iprs.firstName);
  const otherName = upper(iprs.otherName);
  const surnamePart = upper(iprs.surname);
  const surname = [otherName, surnamePart].filter(Boolean).join(' ').trim();

  if (firstName && surname) {
    return { firstName, surname };
  }

  const parts = String(iprs.fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      firstName: firstName || upper(parts[0]),
      surname: parts.slice(1).map((p) => upper(p)).join(' '),
    };
  }

  return {
    firstName: firstName || upper(parts[0]),
    surname,
  };
}

/** Apply IPRS name/DOB onto a policyholder or traveller row. */
export function applyIprsToTravellerRow(row, iprs, { keepDateOfBirth = false } = {}) {
  if (!iprs) return row;
  const { firstName, surname } = formatIprsTravellerNames(iprs);
  return {
    ...row,
    firstName: firstName || row.firstName || '',
    surname: surname || row.surname || '',
    dateOfBirth: keepDateOfBirth ? row.dateOfBirth : (iprs.dateOfBirth || row.dateOfBirth),
    identityFromIprs: true,
  };
}

/** Adult account holder who is policyholder but may not be travelling (customer + minor). */
export function buildAccountHolderTraveller(profile, { isMain = true, isTraveling = false } = {}) {
  const parts = String(profile?.fullName || '').trim().split(/\s+/).filter(Boolean);
  const firstName = parts[0] || profile?.firstName || '';
  const surname = parts.slice(1).join(' ');
  return {
    firstName,
    surname,
    dateOfBirth: '',
    passport: '',
    isMain,
    isTraveling,
    isAccountHolder: true,
  };
}

/** Restore quote-step DOBs from cache or API payload. */
export function parseQuoteTravellerDobs(source) {
  if (!source) return [];
  if (Array.isArray(source.quoteTravellerDobs)) {
    return source.quoteTravellerDobs.map(normalizeTravelDob).filter(Boolean);
  }
  const rawJson = source.quoteTravellersJson || source.quote_travellers_json;
  if (rawJson) {
    try {
      const parsed = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      if (Array.isArray(parsed)) {
        const fromJson = parsed.map((t) => normalizeTravelDob(t.dateOfBirth)).filter(Boolean);
        if (fromJson.length) return fromJson;
      }
    } catch {
      // ignore malformed JSON
    }
  }
  if (Array.isArray(source.quoteTravellers)) {
    const fromQuoteRows = source.quoteTravellers.map((t) => normalizeTravelDob(t.dateOfBirth)).filter(Boolean);
    if (fromQuoteRows.length) return fromQuoteRows;
  }
  if (Array.isArray(source.travellers)) {
    const fromRows = source.travellers.map((t) => normalizeTravelDob(t.dateOfBirth)).filter(Boolean);
    if (fromRows.length) return fromRows;
  }
  if (source.mainTravellerDob) {
    const main = normalizeTravelDob(source.mainTravellerDob);
    if (main) return [main];
  }
  return [];
}

export function hasTravelPremium(quote) {
  return !!(
    Number(quote?.grossUsd)
    || Number(quote?.premiumUsd)
    || Number(quote?.premium)
    || Number(quote?.kshAmount)
  );
}

/** Fill zone/label from product catalogue when API omits them. */
export function enrichTravelQuoteSnapshot(quote = {}) {
  if (!quote || typeof quote !== 'object') return quote || {};
  const next = { ...quote };
  const product = next.prodCode ? findTravelProduct(next.prodCode) : null;
  if (!next.zone && product?.zone) next.zone = product.zone;
  if (!next.productLabel && product?.label) next.productLabel = product.label;
  if (!next.region && product?.region) next.region = product.region;
  if (!next.planType && product?.planType) next.planType = product.planType;
  return next;
}

/** Normalize quotations-api travel quote payload (raw or mapped). */
export function normalizeTravelQuoteApi(dto) {
  if (!dto || typeof dto !== 'object') return null;
  const premiumUsd = dto.premiumUsd ?? dto.premium ?? dto.grossUsd;
  const fxRate = dto.fxRate ?? dto.fx_rate ?? 130;
  const kshAmount = dto.kshAmount ?? dto.ksh_amount
    ?? (premiumUsd != null ? usdToKsh(premiumUsd, fxRate) : null);
  const normalized = {
    quoteId: dto.quoteId,
    prodCode: dto.prodCode || dto.prod_code,
    productLabel: dto.productLabel || dto.planType || dto.plan_type,
    region: dto.region,
    zone: dto.zone,
    planType: dto.planType || dto.plan_type,
    destinationCountry: dto.destinationCountry || dto.destination_country,
    startDate: dto.travelStartDate || dto.startDate || dto.travel_start_date,
    endDate: dto.travelEndDate || dto.endDate || dto.travel_end_date,
    travel_start_date: dto.travelStartDate || dto.travel_start_date,
    travel_end_date: dto.travelEndDate || dto.travel_end_date,
    travellerCount: dto.travellerCount ?? dto.traveller_count ?? 1,
    traveller_count: dto.travellerCount ?? dto.traveller_count ?? 1,
    grossUsd: premiumUsd,
    premiumUsd,
    premium: premiumUsd,
    fxRate,
    fx_rate: fxRate,
    kshAmount,
    ksh_amount: kshAmount,
    email: dto.email,
    email_address: dto.email,
    customerEmail: dto.email,
    phone: dto.phone,
    quoteTravellersJson: dto.quoteTravellersJson,
    mainTravellerDob: dto.mainTravellerDob,
    travellersJson: dto.travellersJson,
    agnShtDesc: dto.agnShtDesc,
    stepCode: dto.stepCode,
    paymentReference: dto.paymentReference,
  };
  const dobs = parseQuoteTravellerDobs(normalized);
  if (dobs.length) normalized.quoteTravellerDobs = dobs;
  if (!normalized.travellers && normalized.quoteTravellersJson) {
    try {
      const parsed = JSON.parse(normalized.quoteTravellersJson);
      if (Array.isArray(parsed)) normalized.travellers = parsed;
    } catch {
      // ignore
    }
  }
  return enrichTravelQuoteSnapshot(normalized);
}

export function mergeTravelQuoteSnapshot(cached, apiData, quoteId) {
  const cache = cached || {};
  const api = apiData || {};
  const grossUsd = api.premiumUsd ?? api.premium ?? api.grossUsd
    ?? cache.grossUsd ?? cache.premiumUsd ?? cache.premium;
  const fxRate = api.fxRate ?? api.fx_rate ?? cache.fxRate ?? 130;
  const kshAmount = api.kshAmount ?? api.ksh_amount ?? cache.kshAmount
    ?? (grossUsd != null ? usdToKsh(grossUsd, fxRate) : null);
  const parsedDobs = (cache.quoteTravellerDobs && cache.quoteTravellerDobs.length)
    ? cache.quoteTravellerDobs
    : parseQuoteTravellerDobs({ ...api, quoteTravellersJson: api.quoteTravellersJson || cache.quoteTravellersJson });
  return enrichTravelQuoteSnapshot({
    ...cache,
    ...api,
    quoteId: cache.quoteId || api.quoteId || quoteId,
    grossUsd,
    premiumUsd: grossUsd,
    premium: grossUsd,
    fxRate,
    kshAmount,
    productLabel: cache.productLabel || api.productLabel || api.plan_type || api.planType,
    travellerCount: api.travellerCount ?? api.traveller_count ?? cache.travellerCount ?? 1,
    startDate: cache.startDate || api.startDate || api.travel_start_date,
    endDate: cache.endDate || api.endDate || api.travel_end_date,
    prodCode: cache.prodCode || api.prodCode || api.prod_code,
    zone: api.zone ?? cache.zone,
    coverFor: cache.coverFor || api.coverFor,
    accountHolderTravelling: inferAccountHolderTravelling({ ...cache, ...api }),
    destinationCountry: cache.destinationCountry || api.destinationCountry || api.destination_country,
    quoteTravellersJson: api.quoteTravellersJson || cache.quoteTravellersJson,
    travellers: (cache.travellers && cache.travellers.length)
      ? cache.travellers
      : api.travellers,
    quoteTravellerDobs: parsedDobs,
    pricingData: cache.pricingData || api.pricingData,
    mapfrePayload: cache.mapfrePayload || api.mapfrePayload,
    summaryLines: cache.summaryLines || api.summaryLines,
    customerEmail: cache.customerEmail || api.customerEmail || api.email_address || api.email,
    accountHolderDob: cache.accountHolderDob || api.accountHolderDob,
    accountHolderFirstName: cache.accountHolderFirstName || api.accountHolderFirstName,
    accountHolderSurname: cache.accountHolderSurname || api.accountHolderSurname,
  });
}

/** Mapfre getPrice at quote — real DOB per traveller, placeholder names until sale. */
export function buildQuoteInsureds(travellers = []) {
  return (travellers || []).map((t, i) => ({
    firstName: 'Traveller',
    surname: String(i + 1),
    dateOfBirth: normalizeTravelDob(t.dateOfBirth) || '',
    passport: `TBC${i + 1}`,
    isMain: !!t.isMain,
    isTraveling: t.isTraveling !== false,
  }));
}

/**
 * Plan-step insured list — separate adult policyholder when account holder is not travelling
 * or when only minors travel (no travelling adult).
 */
export function buildPlanStageInsureds({
  accountHolderTravelling = true,
  coverFor,
  quoteTravellers = [],
  accountHolderDob,
} = {}) {
  const holderNotTravelling = accountHolderTravelling === false
    || coverFor === COVER_FOR_OTHER;
  const separateHolder = (holderNotTravelling || needsSeparateAdultPolicyholder(quoteTravellers))
    && accountHolderDob
    && isTravelAdult(accountHolderDob);
  if (separateHolder) {
    const holder = {
      firstName: 'Policyholder',
      surname: 'One',
      dateOfBirth: accountHolderDob,
      passport: 'TBCPH',
      isMain: true,
      isTraveling: false,
    };
    const travelling = buildQuoteInsureds(
      (quoteTravellers || []).map((t) => ({ ...t, isMain: false, isTraveling: true })),
    );
    return [holder, ...travelling];
  }
  return buildQuoteInsureds(reconcileTravelPolicyholder(quoteTravellers));
}

/** Fill missing name/passport for live repricing before sale details are complete. */
export function withTravelPricingPlaceholders(insureds = []) {
  let travellerIdx = 0;
  return (insureds || []).map((row) => {
    const traveling = row.isTraveling !== false;
    let idx = 0;
    if (traveling) {
      idx = travellerIdx;
      travellerIdx += 1;
    }
    const firstName = String(row.firstName || '').trim()
      || (traveling ? 'Traveller' : 'Policyholder');
    const surname = String(row.surname || '').trim()
      || (traveling ? String(idx + 1) : 'One');
    const passport = resolveMapfrePassport(
      row.passport,
      traveling ? `TBC${idx + 1}` : 'TBCPH',
    );
    return {
      ...row, firstName, surname, passport,
    };
  });
}

/** True when DOBs are enough to call Mapfre getPrice (names/passport can be placeholders). */
export function canRepriceTravelFromDobs(insureds = []) {
  const list = insureds || [];
  const holder = findPolicyholderTraveller(list);
  if (!holder?.dateOfBirth || !isTravelAdult(holder.dateOfBirth)) return false;
  const traveling = list.filter((t) => t.isTraveling !== false);
  if (!traveling.length) return false;
  return traveling.every((t) => t.dateOfBirth && isValidPastDob(t.dateOfBirth));
}

/** @deprecated Use buildQuoteInsureds with DOBs from quote form */
export function buildPlaceholderInsureds(count = 1) {
  const n = Math.min(Math.max(Number(count) || 1, 1), 10);
  return Array.from({ length: n }, (_, i) => ({
    ...emptyQuoteTraveller(i === 0),
    dateOfBirth: '1990-01-15',
  })).map((t) => buildQuoteInsureds([t])[0]);
}

export function durationLabelFromDates(startDate, endDate) {
  const days = travelDaysBetween(startDate, endDate);
  return days > 0 ? `${days} days` : '';
}

/**
 * Build Mapfre getPrice / issue payload (Pacis rates).
 */
export function buildMapfreTravelPayload({
  prodCode,
  zone,
  destinationCountry,
  startDate,
  endDate,
  agnShtDesc,
  email,
  insureds,
  skipPolicyholderValidation = false,
}) {
  const list = insureds || [];
  const traveling = list.filter((i) => i.isTraveling !== false);
  if (!traveling.length) {
    throw new Error('At least one travelling insured is required');
  }
  const main = findPolicyholderTraveller(list);
  if (!main) {
    throw new Error('Policyholder is required');
  }
  if (!skipPolicyholderValidation) {
    const holderCheck = validateTravelPolicyholder(list);
    if (!holderCheck.valid) {
      throw new Error(holderCheck.message);
    }
  }

  const insuredData = traveling.map((i, idx) => ({
    insuredFirstName: String(i.firstName || '').trim(),
    insuredSurname: String(i.surname || '').trim(),
    insuredDateOfBirth: i.dateOfBirth,
    insuredPassport: resolveMapfrePassport(i.passport, `TBC${idx + 1}`),
  }));

  return {
    prodCode,
    policyNumber: 'AUTO',
    startDate,
    endDate,
    agnShtDesc: agnShtDesc || 'DIRECT',
    email: email || undefined,
    zone: getMapfreDestinationZone(destinationCountry, zone),
    destinationCountry,
    insuredData,
    policyholderFirstName: String(main.firstName || '').trim(),
    policyholderSurname: String(main.surname || '').trim(),
    policyholderPassport: resolveMapfrePassport(main.passport, 'TBCPH'),
    policyholderDateOfBirth: main.dateOfBirth,
    coverageLimits: getDefaultCoverageLimits(prodCode),
    // Mapfre getPrice expects a non-zero placeholder in txtPrecioBrutoTotal; actual premium comes back in the response.
    premium: '1',
  };
}
